import { format } from "date-fns";

import type { LineageNode } from "src/graphql";

// The usage summary counts dependents to the API's LINEAGE_MAX_TRAVERSAL_DEPTH,
// so every export walks the same depth and lists the resources the summary counts.
export const LINEAGE_EXPORT_MAX_DEPTH = 10;

export const DEPENDENCY_EXPORT_HEADERS = [
  "Root Resource ID",
  "Root Resource Type",
  "Root Resource Name",
  "Root Resource URL",
  "Dependent Resource ID",
  "Dependent Resource Type",
  "Dependent Resource Name",
  "Dependent Resource URL",
  "Depth",
  "Dependency Via ID",
  "Dependency Via Type",
  "Dependency Via Name",
  "Dependency Via URL",
];

export type LineageExportNode = Pick<
  LineageNode,
  | "id"
  | "resourceType"
  | "resourceId"
  | "kind"
  | "exists"
  | "name"
  | "locationData"
>;

export type LineageExportRef = Pick<
  LineageExportNode,
  "resourceType" | "resourceId"
>;

/** What an export knows about the resource its dependents hang off. */
export type LineageExportRoot = LineageExportRef & {
  name: string | null;
  /** Empty when nothing addresses the root. */
  url: string;
};

export type LineageDependent<TNode> = {
  node: TNode;
  /** False lets the walk skip reading a resource nothing depends on. */
  hasDependents: boolean;
};

/**
 * Reads the direct dependents of each parent, in the order the parents were
 * given. A walk can read from an in-memory graph or page through the API.
 */
export type ReadLineageDependents<TNode> = (
  parents: readonly LineageExportRef[],
) =>
  | ReadonlyArray<ReadonlyArray<LineageDependent<TNode>>>
  | Promise<ReadonlyArray<ReadonlyArray<LineageDependent<TNode>>>>;

export type ReachedDependent<TNode> = {
  node: TNode;
  /** Edges walked from the root, so a direct dependent is 1. */
  depth: number;
  /** Null for a direct dependent, which is reached through nothing. */
  via: TNode | null;
};

export function lineageRefKey({
  resourceType,
  resourceId,
}: LineageExportRef): string {
  return `${resourceType}:${resourceId}`;
}

export function compareLineageNodes(
  a: Pick<LineageExportNode, "resourceType" | "resourceId" | "name">,
  b: Pick<LineageExportNode, "resourceType" | "resourceId" | "name">,
): number {
  return (
    a.resourceType.localeCompare(b.resourceType) ||
    (a.name ?? "").localeCompare(b.name ?? "") ||
    a.resourceId.localeCompare(b.resourceId)
  );
}

/**
 * Every resource downstream of the root, walked breadth first so each one is
 * reported once, at its shortest depth and through the resource on that path.
 * Sorted by depth, then type, name, and ID.
 */
export async function collectLineageDependents<
  TNode extends Pick<LineageExportNode, "resourceType" | "resourceId" | "name">,
>({
  root,
  readDependents,
  maxDepth = LINEAGE_EXPORT_MAX_DEPTH,
}: {
  root: LineageExportRef;
  readDependents: ReadLineageDependents<TNode>;
  maxDepth?: number;
}): Promise<ReachedDependent<TNode>[]> {
  const visited = new Set<string>([lineageRefKey(root)]);
  const reached: ReachedDependent<TNode>[] = [];
  let parents: Array<{ ref: LineageExportRef; node: TNode | null }> = [
    { ref: root, node: null },
  ];

  for (let depth = 1; depth <= maxDepth && parents.length > 0; depth += 1) {
    const dependentsByParent = await readDependents(
      parents.map((parent) => parent.ref),
    );

    const next: typeof parents = [];
    parents.forEach((parent, index) => {
      for (const dependent of dependentsByParent[index] ?? []) {
        const key = lineageRefKey(dependent.node);
        if (visited.has(key)) {
          continue;
        }
        visited.add(key);
        reached.push({ node: dependent.node, depth, via: parent.node });
        if (dependent.hasDependents) {
          next.push({ ref: dependent.node, node: dependent.node });
        }
      }
    });
    parents = next;
  }

  return reached.sort(
    (a, b) => a.depth - b.depth || compareLineageNodes(a.node, b.node),
  );
}

/** One row per dependent, in DEPENDENCY_EXPORT_HEADERS order. */
export function buildDependencyRows<TNode extends LineageExportNode>({
  root,
  dependents,
  resourceUrl,
}: {
  root: LineageExportRoot;
  dependents: readonly ReachedDependent<TNode>[];
  /** Absolute link to a resource, or an empty string when nothing addresses it. */
  resourceUrl: (node: TNode) => string;
}): string[][] {
  return dependents.map(({ node, depth, via }) => [
    root.resourceId,
    root.resourceType,
    root.name ?? "",
    root.url,
    node.resourceId,
    node.resourceType,
    node.name ?? "",
    resourceUrl(node),
    String(depth),
    via?.resourceId ?? "",
    via?.resourceType ?? "",
    via?.name ?? "",
    via ? resourceUrl(via) : "",
  ]);
}

/**
 * Names an export after what it was taken from and when, so exports of
 * different resources or rebuilds do not overwrite each other in the downloads
 * folder.
 */
export function buildLineageExportFilename({
  subject,
  kind,
  extension,
  exportedAt,
}: {
  subject: string;
  kind: string;
  extension: "csv" | "xlsx";
  exportedAt: Date;
}): string {
  const slug = subject.toLowerCase().replace(/[^a-z0-9]/g, "_");
  return `${slug}-${kind}-${format(exportedAt, "yyyyMMddHHmmss")}.${extension}`;
}
